import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Banner from './Banner';
import Button from './buttons/Button';
import demoData from '../data/demoData';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import CodeIcon from '@mui/icons-material/Code';
import '../index.css';

export default function Demo() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [exportSuccess, setExportSuccess] = useState('');
  const [oldSchedule, setOldSchedule] = useState('');
  const [newSchedule, setNewSchedule] = useState('');

  // Load HTML schedules
  React.useEffect(() => {
    fetch('/old_course.html')
      .then(response => response.text())
      .then(content => setOldSchedule(content));

    fetch('/modified_course.html')
      .then(response => response.text())
      .then(content => setNewSchedule(content));
  }, []);

  const handleCopyKey = () => {
    navigator.clipboard.writeText(demoData.sharingKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = (format) => {
    setExportSuccess(`${format} export successful! (Demo Mode)`);
    setTimeout(() => setExportSuccess(''), 3000);
  };

  const navigateBack = () => {
    navigate('/');
  };

  const tryFullVersion = () => {
    navigate('/');
  };

  const text = "Experience CourseCraft.ai in action! This demo shows the complete workflow from Spring 2024 to Fall 2025.";

  return (
    <div className="d-flex flex-column" style={{ backgroundColor: '#003262', minHeight: '100vh' }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between px-4 py-2">
        <Button onClick={navigateBack} icon={<ArrowBackIcon />} text={"Back to Home"} />
        <div className="badge bg-warning text-dark px-3 py-2" style={{ fontSize: '0.9rem' }}>
          🎬 DEMO MODE - No Backend Required
        </div>
      </div>

      {/* Banner */}
      <Banner text={text} />

      {/* Demo Info Section */}
      <div className="container mt-4">
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="card bg-light">
              <div className="card-body">
                <h5 className="card-title text-primary">📥 Input Schedule</h5>
                <p className="mb-1"><strong>Course:</strong> {demoData.inputCourse.department}</p>
                <p className="mb-1"><strong>Semester:</strong> {demoData.inputCourse.semester} {demoData.inputCourse.year}</p>
                <p className="mb-1"><strong>Instructor:</strong> {demoData.inputCourse.instructor}</p>
                <p className="mb-0 text-muted small">{demoData.inputCourse.fullName}</p>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <h5 className="card-title">📤 Generated Schedule</h5>
                <p className="mb-1"><strong>Course:</strong> {demoData.generatedCourse.department}</p>
                <p className="mb-1"><strong>Semester:</strong> {demoData.generatedCourse.semester} {demoData.generatedCourse.year}</p>
                <p className="mb-1"><strong>Instructor:</strong> {demoData.generatedCourse.instructor}</p>
                <p className="mb-0 small opacity-75">{demoData.generatedCourse.fullName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="card bg-white mb-4">
          <div className="card-body">
            <h4 className="text-center mb-4">✨ What CourseCraft.ai Did</h4>
            <div className="row">
              {demoData.features.map((feature, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="d-flex">
                    <div className="me-3" style={{ fontSize: '2rem' }}>{feature.icon}</div>
                    <div>
                      <h6 className="mb-1">{feature.title}</h6>
                      <p className="mb-0 small text-muted">{feature.description}</p>
                      {feature.example && (
                        <p className="mb-0 small text-primary mt-1">Example: {feature.example}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Schedule Comparison */}
        <div className="card bg-white mb-4">
          <div className="card-body">
            <h4 className="text-center mb-4">📊 Schedule Comparison</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <h5 className="text-center text-warning">{demoData.scheduleComparison.oldSemester}</h5>
                <div
                  className="border rounded p-2 overflow-auto"
                  style={{ maxHeight: '400px', backgroundColor: '#f8f9fa' }}
                  dangerouslySetInnerHTML={{ __html: oldSchedule }}
                />
              </div>
              <div className="col-md-6 mb-3">
                <h5 className="text-center text-success">{demoData.scheduleComparison.newSemester}</h5>
                <div
                  className="border rounded p-2 overflow-auto"
                  style={{ maxHeight: '400px', backgroundColor: '#f8f9fa' }}
                  dangerouslySetInnerHTML={{ __html: newSchedule }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="card bg-white mb-4">
          <div className="card-body">
            <h4 className="text-center mb-4">📤 Export Options</h4>
            <div className="row">
              {demoData.exportFormats.map((format, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="card h-100">
                    <div className="card-body text-center">
                      <div style={{ fontSize: '3rem' }}>{format.icon}</div>
                      <h5 className="mt-2">{format.name}</h5>
                      <p className="small text-muted">{format.description}</p>
                      {format.count && (
                        <p className="badge bg-primary">{format.count}</p>
                      )}
                      {format.name === 'Sharing Key' ? (
                        <div className="mt-2">
                          <code className="d-block p-2 bg-light rounded small mb-2">
                            {demoData.sharingKey}
                          </code>
                          <div className="d-flex justify-content-center">
                            <Button
                              onClick={handleCopyKey}
                              icon={<ShareIcon />}
                              text={copied ? "Copied!" : "Copy Key"}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center">
                          <Button
                            onClick={() => handleExport(format.name)}
                            icon={format.name === 'Google Calendar' ? <CalendarMonthIcon /> :
                                  format.name === 'ICS File' ? <DownloadIcon /> : <CodeIcon />}
                            text={`Export to ${format.name}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {exportSuccess && (
              <div className="alert alert-success text-center mt-3" role="alert">
                {exportSuccess}
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="card bg-white mb-4">
          <div className="card-body">
            <h4 className="text-center mb-4">📈 Schedule Statistics</h4>
            <div className="row text-center">
              <div className="col-md-3 mb-2">
                <h2 className="text-primary">{demoData.stats.totalWeeks}</h2>
                <p className="text-muted">Weeks</p>
              </div>
              <div className="col-md-3 mb-2">
                <h2 className="text-primary">{demoData.stats.lectures}</h2>
                <p className="text-muted">Lectures</p>
              </div>
              <div className="col-md-3 mb-2">
                <h2 className="text-primary">{demoData.stats.labs}</h2>
                <p className="text-muted">Labs</p>
              </div>
              <div className="col-md-3 mb-2">
                <h2 className="text-primary">{demoData.stats.totalEvents}</h2>
                <p className="text-muted">Total Events</p>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial */}
        <div className="card bg-warning mb-4">
          <div className="card-body text-center">
            <blockquote className="blockquote mb-0">
              <p className="mb-3 fst-italic">"{demoData.testimonial.quote}"</p>
              <footer className="blockquote-footer">
                {demoData.testimonial.author}, <cite title="Source Title">{demoData.testimonial.semester}</cite>
              </footer>
            </blockquote>
          </div>
        </div>

        {/* CTA */}
        <div className="card bg-primary text-white mb-5">
          <div className="card-body text-center py-5">
            <h3 className="mb-3">Ready to Try the Full Version?</h3>
            <p className="mb-4">Connect your own course data and generate schedules with backend integration</p>
            <div className="d-flex justify-content-center">
              <Button
                onClick={tryFullVersion}
                icon={<CheckIcon />}
                text={"Try Interactive Version"}
              />
            </div>
            <p className="mt-3 small opacity-75">Requires backend setup for full functionality</p>
          </div>
        </div>
      </div>
    </div>
  );
}
